import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';
import { SearchBar } from './SearchBar';
import { CommandPalette } from './CommandPalette';
import { ToastProvider } from './Toast';
import { ConfirmProvider } from './ConfirmDialog';
import { NotificationBell } from '@/components/NotificationBell';
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal';
import { useAuthStore } from '@/stores/auth';
import { useSocketStore } from '@/stores/socket';
import { authApi } from '@/services/api';
import { configApi } from '@/services/config.api';
import { useHotkey } from '@/hooks/useHotkey';

export function AppLayout({
  children,
  fullBleed = false,
}: {
  children: ReactNode;
  /**
   * Désactive le conteneur `max-w-7xl px-6 py-8` et laisse le contenu
   * occuper toute la hauteur/largeur du `<main>`. Utilisé par la carte
   * cartographique qui a besoin d'un viewport complet pour Leaflet.
   */
  fullBleed?: boolean;
}) {
  // État éphémère uniquement - pas de persistance navigateur (contraintes
  // navigateur du projet). Si on veut persister, passer par un cookie signé
  // ou une préférence user BDD.
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [oppModalOpen, setOppModalOpen] = useState(false);
  const navigate = useNavigate();

  useHotkey('mod+k', () => setPaletteOpen(true), { allowInInput: true });
  // Raccourci `/` pour focus la recherche - uniquement hors input (sinon on
  // bloquerait le caractère `/` dans les champs texte).
  useHotkey('/', () => setPaletteOpen(true), { allowInInput: false });

  // Cycle de vie du WebSocket : connexion tant qu'un access token est posé.
  // AppLayout n'est monté que sur des routes authentifiées - rafraîchir la
  // connexion quand le token change (refresh rotation).
  const token = useAuthStore((s) => s.accessToken);
  const connectSocket = useSocketStore((s) => s.connect);
  const disconnectSocket = useSocketStore((s) => s.disconnect);
  useEffect(() => {
    if (token) connectSocket(token);
    return () => disconnectSocket();
  }, [token, connectSocket, disconnectSocket]);

  // Mode démonstration : fetch `demo_mode.enabled` via l'endpoint public.
  // On ne cache pas le résultat en-dehors de cet AppLayout - le bandeau
  // apparaît/disparaît instantanément quand un admin toggle le flag (après
  // un refresh de page). Un invalidate manuel serait possible via un store
  // partagé ; V1 on reste simple.
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => {
    if (!token) return;
    configApi
      .getPublicSettings(token)
      .then((s) => setDemoMode(s['demo_mode.enabled'] === true))
      .catch(() => setDemoMode(false));
  }, [token]);

  return (
    <ToastProvider>
      <ConfirmProvider>
        <div className="flex h-[calc(100vh/var(--app-zoom))] flex-col overflow-hidden bg-bg-page font-sans text-text-primary">
          {demoMode && <DemoBanner />}
          <div className="flex min-h-0 flex-1">
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
            <div className="flex min-w-0 flex-1 flex-col">
              <AppHeader onSearch={() => setPaletteOpen(true)} />
              <main
                className={cn('flex-1', fullBleed ? 'relative overflow-hidden' : 'overflow-y-auto')}
              >
                {fullBleed ? (
                  children
                ) : (
                  <div className="mx-auto max-w-7xl px-6 py-8">{children}</div>
                )}
              </main>
            </div>
          </div>
        </div>
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onCreateOpportunity={() => setOppModalOpen(true)}
        />
        {oppModalOpen && (
          <CreateOpportunityModal
            onClose={() => setOppModalOpen(false)}
            onCreated={(opp) => {
              setOppModalOpen(false);
              // Deep-link direct vers la fiche prospect, onglet Opportunités,
              // opp ouverte - évite le hop par le redirector.
              navigate(`/prospects/${opp.prospect.id}?tab=opportunities&opportunity=${opp.id}`);
            }}
          />
        )}
      </ConfirmProvider>
    </ToastProvider>
  );
}

/**
 * Bandeau « Mode démonstration » - visible sur toutes les pages quand le
 * flag `demo_mode.enabled` est posé à `true` dans `config_settings`. Rend
 * explicite l'environnement de démo pour éviter toute confusion avec la
 * prod (cf SPECIFICATIONS.md §13, ROADMAP étape 18).
 */
function DemoBanner() {
  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-2 bg-amber-400 px-4 py-2 text-center text-sm font-bold text-amber-950"
    >
      <AlertTriangle className="h-4 w-4" />
      <span>MODE DÉMONSTRATION - Données fictives</span>
    </div>
  );
}

function AppHeader({ onSearch }: { onSearch: () => void }) {
  return (
    <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-border-subtle bg-surface px-6">
      <div className="flex flex-1 justify-center">
        <SearchBar onOpen={onSearch} />
      </div>
      <NotificationBell />
      <UserMenu />
    </header>
  );
}

function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  async function handleLogout() {
    try {
      await authApi.logout();
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-gray-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
          {initials}
        </div>
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-lg border border-border-subtle bg-white shadow-popover animate-scale-in',
          )}
        >
          <div className="border-b border-border-subtle px-3 py-2.5">
            <div className="truncate text-sm font-medium text-brand">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="truncate text-xs text-text-muted">{user?.email}</div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full px-3 py-2 text-left text-sm text-brand hover:bg-gray-50"
            role="menuitem"
          >
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
}
